import {
  buildActionCardProps,
  getInformationalCardKind,
  NOT_RELEVANT_DISMISSAL,
  useActionCard,
  useDeferredDismiss,
  type ActionCardCallbacks,
  type ActionCardSurface,
  AgentTeamContentSchema,
} from '@littlebird/action-cards';
import {
  getSDK,
  type ActionCard,
  type ActionCardDismissal,
} from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useState, type ReactNode } from 'react';

import { AgentTeamCard } from '@/features/agents/agents-team-card';
import { isAgentsUxEnabled } from '@/features/agents/agents.utils';
import { HivemindChatCard } from '@/features/hivemind/conversations/hivemind-chat-card';
import { trackEvent } from '@/utils/analytics';

import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
  trackActionCardShown,
  trackDismissal,
} from '../analytics';
import { surfaceOutcome } from '../commands';
import { useDismissToast } from '../use-dismiss-toast';

import { ActionCardRow } from './ActionCardRow';
import { CardV2Item, type CardV2ItemLayout } from './CardV2Item';
import { CodingAgentCard } from './CodingAgentCard';

const surfacing = async (run: ReturnType<ActionCardCallbacks['onConfirm']>) => {
  const outcome = await run;
  if (outcome) surfaceOutcome(outcome);
  return outcome;
};

type RowExpansion = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
};

type ActionCardItemProps = {
  card: ActionCard;
  sourceChatTag?: ReactNode;
  surface?: ActionCardSurface;
  rowExpansion?: RowExpansion;
  onAskUserComplete?: () => void;
  openInCardChat?: (seed?: string) => void;
  onDismiss?: (dismissal?: ActionCardDismissal) => void;
  /** Off for a pane that swaps one card for the next, which shows each card at once */
  animateIn?: boolean;
  layout?: CardV2ItemLayout;
};

const CANVAS_SURFACE = 'canvas';

const trackLegacyRunCompletion = (
  cardId: string,
  analytics: Record<string, unknown>,
  outcome: Awaited<ReturnType<ActionCardCallbacks['onConfirm']>> | undefined
) => {
  if (outcome?.status !== 'succeeded') return;
  const status = getSDK().stores.actionCards.get(cardId)?.status;
  if (status !== 'completed') return;
  trackEvent('ACTION_CARD_COMPLETED', {
    ...analytics,
    status,
    completion_method: 'run',
    completion_confirmation: 'server_terminal',
  });
};

// Canvas keeps the old row until the new card is checked there
const CanvasActionCardRow = observer(
  ({
    card,
    rowExpansion,
  }: {
    card: ActionCard;
    rowExpansion?: RowExpansion;
  }) => {
    const result = useActionCard(card);
    const deferredDismiss = useDeferredDismiss({
      onEvent: trackDismissal(CANVAS_SURFACE),
      onOutcome: surfaceOutcome,
    });
    useDismissToast(deferredDismiss);
    const reducedMotion = useReducedMotion();
    const [internalExpanded, setInternalExpanded] = useState(false);
    const expanded = rowExpansion?.expanded ?? internalExpanded;
    const onExpandedChange =
      rowExpansion?.onExpandedChange ?? setInternalExpanded;

    useEffect(() => {
      if (!result.renderable) return;
      trackActionCardShown(card, CANVAS_SURFACE, 'full', {
        card_expanded: expanded,
      });
    }, [card, expanded, result.renderable]);

    if (
      !result.renderable ||
      deferredDismiss.isDeferred(card.id) ||
      result.state === 'dismissed'
    )
      return null;
    const dismissTargets = [
      {
        cardId: card.id,
        actionType: card.actionType,
        cardKind: getInformationalCardKind(card),
        origin: card.origin,
        analytics: actionCardAnalyticsSnapshot(card, CANVAS_SURFACE, {
          card_expanded: expanded,
        }),
      },
    ];
    const callbacks = {
      ...result.callbacks,
      onEdit: () => {
        const context = actionCardAnalyticsContext(CANVAS_SURFACE, {
          card_expanded: expanded,
        });
        trackEvent('ACTION_CARD_EDIT_STARTED', {
          ...card.analytics,
          ...buildActionCardProps(card, context),
        });
        result.callbacks.onEdit();
      },
      onSaveEdit: () => {
        const context = actionCardAnalyticsContext(CANVAS_SURFACE, {
          card_expanded: expanded,
        });
        const analytics = {
          ...card.analytics,
          ...buildActionCardProps(card, context),
        };
        void surfacing(result.callbacks.onSaveEdit()).then((outcome) => {
          // Saved means saved: a rejected or failed save records nothing
          if (outcome?.status !== 'succeeded') return;
          trackEvent('ACTION_CARD_EDIT_SAVED', analytics);
        });
      },
      onConfirm: () => {
        const context = actionCardAnalyticsContext(CANVAS_SURFACE, {
          card_expanded: expanded,
        });
        const analytics = {
          ...card.analytics,
          ...buildActionCardProps(card, { ...context, retry: false }),
          action_intent: 'external_execution',
        };
        trackEvent('ACTION_CARD_CONFIRM_SUBMITTED', analytics);
        void surfacing(result.callbacks.onConfirm()).then(
          (outcome) => {
            trackLegacyRunCompletion(card.id, analytics, outcome);
          },
          () => {}
        );
      },
      onRetry: () => {
        const context = actionCardAnalyticsContext(CANVAS_SURFACE, {
          card_expanded: expanded,
        });
        const analytics = {
          ...card.analytics,
          ...buildActionCardProps(card, { ...context, retry: true }),
          action_intent: 'external_execution',
        };
        trackEvent('ACTION_CARD_CONFIRM_SUBMITTED', analytics);
        void surfacing(result.callbacks.onRetry()).then(
          (outcome) => {
            trackLegacyRunCompletion(card.id, analytics, outcome);
          },
          () => {}
        );
      },
      // The toast owns Undo while the controller delays the dismiss request
      onDismiss: () => {
        deferredDismiss.beginDismiss(dismissTargets);
      },
      onDismissNotRelevant: () => {
        deferredDismiss.beginDismiss(dismissTargets, NOT_RELEVANT_DISMISSAL);
      },
    };
    return (
      <motion.div
        className="w-full"
        initial={reducedMotion ? false : { opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.18, ease: 'easeOut' }}
      >
        <ActionCardRow
          content={result.content}
          state={result.state}
          expanded={expanded}
          externalUrl={result.externalUrl}
          externalRef={result.externalRef}
          revision={result.revision}
          isRevisionEdit={result.isRevision}
          failureMessage={result.failureMessage}
          completionResult={result.completionResult}
          context={result.context}
          sourceLink={result.sourceLink}
          callbacks={callbacks}
          onExpandedChange={onExpandedChange}
          onContentChange={result.onContentChange}
        />
      </motion.div>
    );
  }
);

export const ActionCardItem = observer((props: ActionCardItemProps) => {
  if (props.card.actionType === 'agent_team' && isAgentsUxEnabled()) {
    const content = AgentTeamContentSchema.safeParse(props.card.content).data;
    return content ? (
      <AgentTeamCard
        key={`${props.card.id}-${props.card.updatedAt.getTime()}`}
        card={props.card}
        content={content}
        sourceChatTag={props.sourceChatTag}
      />
    ) : null;
  }
  if (props.card.actionType === 'coding_agent')
    return (
      <CodingAgentCard card={props.card} sourceChatTag={props.sourceChatTag} />
    );
  if (props.surface === 'canvas')
    return (
      <CanvasActionCardRow
        card={props.card}
        rowExpansion={props.rowExpansion}
      />
    );
  const card = (
    <CardV2Item
      card={props.card}
      surface={props.surface}
      sourceChatTag={props.sourceChatTag}
      onAskUserComplete={props.onAskUserComplete}
      openInCardChat={props.openInCardChat}
      onDismiss={props.onDismiss}
      animateIn={props.animateIn}
      layout={props.layout}
    />
  );
  // Every chat draws its cards here, so a message to several people is one card in each
  return props.card.actionType === 'teammate_message' && !props.surface ? (
    <HivemindChatCard card={props.card}>{card}</HivemindChatCard>
  ) : (
    card
  );
});
